import { FlexColumn, FlexRow, StyledBanner, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { HeadingXSmall, ParagraphXSmall } from 'baseui/typography';
import { memo, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { postCommand } from '../../../services/commandService';
import { accountState } from '../../../storage/account';
import { commandFieldsState, selectedCommandState } from '../../../storage/commands';
import { Asset } from '../../../types/asset';
import { AvailableCommand, CommandCategory, DeviceCommands } from '../../../types/availableCommand';
import { AssetPicker } from '../../shared/AssetPicker';
import { LabeledSelect } from '../../ui/LabeledSelect';
import { useCommands } from '../hooks/useCommands';
import { CommandFields } from './CommandFields';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  asset?: Asset;
  deviceIdent?: string;
  commandCategory?: CommandCategory;
};

function CommandModal({ onClose, isOpen, asset, deviceIdent, commandCategory }: Props) {
  const account = useRecoilValue(accountState);
  const [selectedAsset, setSelectedAsset] = useState<Asset>();
  const [commandSelected, setCommandSelected] = useRecoilState(selectedCommandState);
  const commandFields = useRecoilValue(commandFieldsState);
  const [devices, setDevices] = useState<DeviceCommands[]>();
  const [deviceSelected, setDeviceSelected] = useState<DeviceCommands>();
  const [error, setError] = useState('');
  const { theme, classes } = useStyles();
  const { showPositiveFeedback, showFailFeedback } = useFeedback();
  const { loading, startLoading, stopLoading } = useLoading();
  const { loading: loadingDevices, startLoading: startLoadingDevices, stopLoading: stopLoadingDevices } = useLoading();
  const { getDevicesCommandsByAsset } = useCommands();
  const requiredExtraFields = commandFields ? Object.values(commandFields).filter((field) => field.required) : [];

  useEffect(() => {
    if (!asset || !isOpen) return;
    setSelectedAsset(asset);
  }, [asset, isOpen]);

  useEffect(() => {
    if (!selectedAsset || !isOpen) {
      setDevices(undefined);
      return;
    }
    const loadDeviceCommands = async () => {
      startLoadingDevices();
      const devices = await getDevicesCommandsByAsset(selectedAsset._id, commandCategory);
      setDevices(devices);
      if (devices) {
        const initialDevice = deviceIdent ? devices.find((device) => device.ident === deviceIdent) : devices[0];
        if (initialDevice) {
          setDeviceSelected(initialDevice);
          const initialCommand = initialDevice.commands[0];
          if (initialCommand) {
            setCommandSelected(initialCommand);
          }
        }
      }
      stopLoadingDevices();
    };

    loadDeviceCommands();
  }, [selectedAsset, isOpen, deviceIdent]);

  const canSendCommand =
    !!commandSelected && requiredExtraFields.every((field) => field.value !== undefined && field.value !== '');

  const handleFieldChange = (key: string, newValue: any) => {
    if (!commandSelected?.fields) return;

    setCommandSelected({
      ...commandSelected,
      fields: {
        ...commandSelected.fields,
        [key]: {
          ...commandSelected.fields[key],
          value: newValue,
        },
      },
    });
  };

  const sendCommand = async () => {
    if (
      !selectedAsset ||
      !commandSelected?.commandName ||
      !deviceSelected?.ident ||
      !account?.client ||
      !selectedAsset.subClient ||
      !canSendCommand
    )
      return;
    startLoading();
    const { commandId, error } = await postCommand(
      selectedAsset._id,
      account.client._id,
      selectedAsset.subClient._id,
      commandSelected.commandName,
      deviceSelected.ident,
      account.subAccount?._id ?? undefined,
      commandFields?.reduce(
        (acc, field) => {
          acc[field.key] = field.value || '';
          return acc;
        },
        {} as { [key: string]: string | number },
      ),
    );
    if (!error && commandId) {
      showPositiveFeedback('El comando ha sido enviado');
      resetModal();
    } else if (typeof error === 'string') {
      setError(error);
    } else {
      showFailFeedback(error || 'Ocurrió un error, inténtalo más tarde.');
    }
    stopLoading();
  };

  const resetModal = () => {
    onClose();
    setSelectedAsset(undefined);
    setDeviceSelected(undefined);
    setCommandSelected(undefined);
    setError('');
  };

  const onAssetChange = (asset: Asset) => {
    setSelectedAsset(asset);
    setDeviceSelected(undefined);
    setCommandSelected(undefined);
  };

  return createPortal(
    <Modal
      animate
      closeable
      onClose={resetModal}
      isOpen={isOpen}
    >
      <ModalHeader>
        <FlexRow
          gap={theme.sizing.scale200}
          classNames={classes.centeredStart}
        >
          <HeadingXSmall margin={0}>Enviar {commandCategory ? 'Indicación' : 'Comando'}</HeadingXSmall>
        </FlexRow>
      </ModalHeader>
      <ModalBody>
        {error && (
          <StyledBanner
            title="*Error*"
            kind="negative"
            action={{
              label: 'Label',
              onClick: () => {
                setError('');
              },
            }}
          >
            {error}
          </StyledBanner>
        )}
        <FlexColumn>
          <AssetPicker
            assetId={selectedAsset?._id}
            onChange={onAssetChange}
            disabled={!!asset}
          />
          <LabeledSelect
            label="Dispositivo:"
            options={devices}
            disabled={!devices || devices.length === 1 || (!!deviceIdent && deviceSelected?.ident === deviceIdent)}
            value={[{ ...deviceSelected }]}
            onChange={(params) => {
              const option = params.option as DeviceCommands;
              setDeviceSelected(option);
              const initialCommand = option.commands[0];
              if (initialCommand) {
                setCommandSelected(initialCommand);
              }
            }}
            labelKey="model"
            valueKey="ident"
            loading={loadingDevices}
          />
          <FlexColumn gap={theme.sizing.scale100}>
            <LabeledSelect
              label="Comando:"
              options={deviceSelected?.commands}
              disabled={!deviceSelected?.commands.length}
              value={[{ ...commandSelected }]}
              onChange={(params) => setCommandSelected(params.option as AvailableCommand)}
              labelKey="commandDescription"
              valueKey="commandDescription"
              loading={loadingDevices}
            />
            {commandSelected?.commandName === 'wakeup' && (
              <ParagraphXSmall
                margin={0}
                color={theme.colors.contentTertiary}
              >
                Validar que la unidad está en modo "Sleep"
              </ParagraphXSmall>
            )}
          </FlexColumn>
          {commandSelected?.fields && (
            <FlexColumn gap={theme.sizing.scale200}>
              {commandFields.map((field) => (
                <CommandFields
                  key={field.key}
                  field={field}
                  onChange={(newValue) => handleFieldChange(field.key, newValue)}
                />
              ))}
            </FlexColumn>
          )}
        </FlexColumn>
      </ModalBody>
      <ModalFooter>
        <FlexRow
          gap={theme.sizing.scale300}
          classNames={classes.centeredEnd}
        >
          <StyledButton
            kind="tertiary"
            onClick={resetModal}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={sendCommand}
            disabled={!canSendCommand}
            isLoading={loading}
          >
            Enviar
          </StyledButton>
        </FlexRow>
      </ModalFooter>
    </Modal>,
    document.body,
  );
}

export default memo(CommandModal);
